import { useCallback, useEffect, useRef, useState } from 'react';
import {
  gpcEnabled,
  onOpenCookieSettings,
  readConsent,
  saveConsent
} from '../services/consent.js';

// Focus trap for the consent dialogs. Keeps Tab cycling inside the dialog
// and restores focus to the previously focused element on close.
function useFocusTrap(active) {
  const containerRef = useRef(null);
  const restoreRef = useRef(null);

  useEffect(() => {
    if (!active || !containerRef.current) return;
    restoreRef.current = document.activeElement;
    const container = containerRef.current;
    const focusables = () =>
      container.querySelectorAll(
        'button:not([disabled]), [href], input:not([disabled]), [tabindex]:not([tabindex="-1"])'
      );
    const first = focusables()[0];
    if (first) first.focus();

    const onKeyDown = (e) => {
      if (e.key !== 'Tab') return;
      const items = Array.from(focusables());
      if (!items.length) return;
      const firstEl = items[0];
      const lastEl = items[items.length - 1];
      if (e.shiftKey && document.activeElement === firstEl) {
        e.preventDefault();
        lastEl.focus();
      } else if (!e.shiftKey && document.activeElement === lastEl) {
        e.preventDefault();
        firstEl.focus();
      }
    };
    container.addEventListener('keydown', onKeyDown);
    return () => {
      container.removeEventListener('keydown', onKeyDown);
      if (restoreRef.current && restoreRef.current.focus) restoreRef.current.focus();
    };
  }, [active]);

  return containerRef;
}

export default function ConsentBanner() {
  const [consent, setConsent] = useState(() => readConsent());
  const [settingsOpen, setSettingsOpen] = useState(false);

  useEffect(() => onOpenCookieSettings(() => setSettingsOpen(true)), []);

  // GPC users are opted out of analytics/marketing automatically,
  // no banner interaction required.
  useEffect(() => {
    if (!consent && gpcEnabled()) {
      setConsent(saveConsent({ analytics: false, marketing: false }));
    }
  }, [consent]);

  const decide = useCallback((analytics, marketing) => {
    setConsent(saveConsent({ analytics, marketing }));
    setSettingsOpen(false);
  }, []);

  if (settingsOpen) {
    return (
      <CookieSettings
        consent={consent}
        onSave={decide}
        onClose={() => setSettingsOpen(false)}
      />
    );
  }

  if (consent) return null;

  return <Banner onDecide={decide} onOpenSettings={() => setSettingsOpen(true)} />;
}

function Banner({ onDecide, onOpenSettings }) {
  const ref = useFocusTrap(true);
  return (
    <div
      ref={ref}
      role="dialog"
      aria-modal="true"
      aria-label="Cookie and privacy notice"
      className="consent-banner"
    >
      <p className="consent-banner__text">
        Eliza uses only strictly necessary cookies and on-device storage — sign-in
        session, offline sync, and your saved preferences. No analytics, advertising,
        or tracking scripts load on this site, before or after any choice you make
        here. Details are in the{' '}
        <a href="/cookies">Cookie Policy</a>.
      </p>
      <div className="consent-banner__actions">
        <button className="consent-btn consent-btn--solid" onClick={() => onDecide(true, true)}>
          Accept All
        </button>
        <button className="consent-btn consent-btn--solid" onClick={() => onDecide(false, false)}>
          Reject All
        </button>
        <button className="consent-btn" onClick={onOpenSettings}>
          Cookie Settings
        </button>
      </div>
    </div>
  );
}

function CookieSettings({ consent, onSave, onClose }) {
  const [analytics, setAnalytics] = useState(Boolean(consent?.analytics));
  const [marketing, setMarketing] = useState(Boolean(consent?.marketing));
  const ref = useFocusTrap(true);
  const gpc = gpcEnabled();

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div className="consent-overlay">
      <div
        ref={ref}
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-settings-title"
        className="consent-settings"
      >
        <div className="consent-settings__head">
          <h2 id="cookie-settings-title">Cookie settings</h2>
          <button className="consent-settings__close" onClick={onClose} aria-label="Close cookie settings">
            ×
          </button>
        </div>

        {gpc && (
          <p className="consent-settings__gpc">
            Global Privacy Control detected — analytics and marketing stay opted out
            automatically on this browser.
          </p>
        )}

        <ConsentRow
          title="Strictly necessary"
          locked
          checked
          description="Sign-in session (AWS Cognito), offline data sync, service-worker cache, and saved app preferences. These keep the app working and can't be switched off."
        />
        <ConsentRow
          title="Analytics"
          checked={analytics && !gpc}
          disabled={gpc}
          onChange={setAnalytics}
          description="Not used on this site. No analytics scripts or cookies exist here; this preference is recorded in case that ever changes."
        />
        <ConsentRow
          title="Marketing"
          checked={marketing && !gpc}
          disabled={gpc}
          onChange={setMarketing}
          description="Not used on this site. No advertising or marketing trackers exist here; this preference is recorded in case that ever changes."
        />

        <div className="consent-settings__actions">
          <button className="consent-btn consent-btn--solid" onClick={() => onSave(analytics, marketing)}>
            Save preferences
          </button>
          <button className="consent-btn" onClick={() => onSave(true, true)}>
            Accept All
          </button>
          <button className="consent-btn" onClick={() => onSave(false, false)}>
            Reject All
          </button>
        </div>
      </div>
    </div>
  );
}

function ConsentRow({ title, description, checked, onChange, locked, disabled }) {
  const id = `consent-${title.toLowerCase().replace(/\s+/g, '-')}`;
  return (
    <div className="consent-row">
      <div className="consent-row__head">
        {locked ? (
          <>
            <span className="consent-row__title">{title}</span>
            <span className="consent-row__locked">Always on</span>
          </>
        ) : (
          <>
            <label htmlFor={id} className="consent-row__title">
              {title}
            </label>
            <input
              id={id}
              type="checkbox"
              className="consent-row__toggle"
              checked={checked}
              disabled={disabled}
              aria-describedby={`${id}-desc`}
              onChange={(e) => onChange(e.target.checked)}
            />
          </>
        )}
      </div>
      <p id={`${id}-desc`} className="consent-row__desc">
        {description}
      </p>
    </div>
  );
}
